import React, { useEffect, useState } from 'react';
import { api } from '../api.js';
import { Upload, Trash2 } from 'lucide-react';
import { useT } from '../i18n/useT.js';
import { useAlert } from '../components/AlertProvider.jsx';

const BYTES_PER_GB = 1024 * 1024 * 1024;
const STORAGE_UNITS = ['B', 'KB', 'MB', 'GB', 'TB'];

function normalizeByteSize(value) {
  const numeric = Number(value || 0);
  return Number.isFinite(numeric) && numeric >= 0 ? numeric : 0;
}

function formatBytes(bytes) {
  const value = normalizeByteSize(bytes);
  if (value === 0) return '0 B';

  let scaled = value;
  let unitIndex = 0;
  while (scaled >= 1024 && unitIndex < STORAGE_UNITS.length - 1) {
    scaled /= 1024;
    unitIndex += 1;
  }

  const digits = scaled >= 10 || unitIndex === 0 ? 0 : 1;
  return `${scaled.toFixed(digits)} ${STORAGE_UNITS[unitIndex]}`;
}

function getAssetByteSize(asset) {
  return normalizeByteSize(asset?.byteSize ?? asset?.byte_size);
}

export default function AssetsPage() {
  const t = useT();
  const { showAlert, showConfirm } = useAlert();
  const [assets, setAssets] = useState([]);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [assetsRes, billingRes] = await Promise.all([
        api.getAssets(),
        api.getMyBilling().catch(() => null),
      ]);
      setAssets(assetsRes.items || []);
      setBilling(billingRes || null);
    } catch {
      setAssets([]);
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const storageUsedBytes = normalizeByteSize(billing?.usageMonthToDate?.storageBytes);
  const storageLimitGb = billing?.limits?.storageGB;
  const hasStorageLimit = storageLimitGb !== undefined && storageLimitGb !== null && Number(storageLimitGb) !== -1;
  const storageLimitBytes = hasStorageLimit ? Math.max(0, Number(storageLimitGb) * BYTES_PER_GB) : -1;
  const storageRemainingBytes = storageLimitBytes >= 0 ? Math.max(0, storageLimitBytes - storageUsedBytes) : -1;
  const storageUsagePercent = storageLimitBytes > 0
    ? Math.min(100, Math.round((storageUsedBytes / storageLimitBytes) * 100))
    : 0;

  const handleUpload = async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = async (e) => {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;

      const selectedBytes = files.reduce((total, file) => total + normalizeByteSize(file.size), 0);
      if (storageRemainingBytes >= 0 && selectedBytes > storageRemainingBytes) {
        showAlert(
          t(
            'assets.storageExceeded',
            `Selected files need ${formatBytes(selectedBytes)} but only ${formatBytes(storageRemainingBytes)} remain on your plan.`
          ),
          {
            title: t('assets.title', 'Assets'),
            tone: 'warning',
          }
        );
        return;
      }

      setUploading(true);
      try {
        for (const file of files) {
          const fd = new FormData();
          fd.append('file', file);
          fd.append('name', file.name);
          await api.uploadAsset(fd);
        }
        await load();
      } catch (err) {
        showAlert(err.message || t('assets.uploadFailed', 'Could not upload asset.'), {
          title: t('assets.title', 'Assets'),
          tone: 'error',
        });
      } finally {
        setUploading(false);
      }
    };
    input.click();
  };

  const handleDelete = async (id) => {
    const accepted = await showConfirm(t('assets.confirmDelete', 'Delete this asset?'), {
      title: t('assets.title', 'Assets'),
      confirmLabel: t('billing.yes', 'Yes'),
      cancelLabel: t('billing.cancel', 'Cancel'),
      tone: 'warning',
    });
    if (!accepted) return;
    try {
      await api.deleteAsset(id);
      await load();
    } catch (err) {
      showAlert(err.message || t('assets.deleteFailed', 'Could not delete asset.'), {
        title: t('assets.title', 'Assets'),
        tone: 'error',
      });
    }
  };

  return (
    <>
      <div className="toolbar">
        <h2>{t('assets.title', 'Assets')}</h2>
        <div className="toolbar-actions">
          <button className="btn-primary" onClick={handleUpload} disabled={uploading}>
            <Upload size={14} /> {uploading ? t('assets.uploading', 'Uploading...') : t('assets.upload', 'Upload')}
          </button>
        </div>
      </div>
      <div className="page-content">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 12,
            marginBottom: 16,
          }}
        >
          <div className="card" style={{ marginBottom: 0, padding: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>{t('assets.storageUsed', 'Storage used')}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>{formatBytes(storageUsedBytes)}</div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
              {hasStorageLimit
                ? t('assets.storageLimit', `${formatBytes(storageUsedBytes)} of ${formatBytes(storageLimitBytes)} used`)
                : t('assets.storageUnlimited', 'Unlimited storage')}
            </div>
          </div>

          <div className="card" style={{ marginBottom: 0, padding: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>{t('assets.storageRemaining', 'Remaining')}</div>
            <div style={{ fontSize: 22, fontWeight: 700 }}>
              {hasStorageLimit ? formatBytes(storageRemainingBytes) : t('assets.storageUnlimitedShort', 'Unlimited')}
            </div>
            {hasStorageLimit && (
              <>
                <div style={{ marginTop: 10, height: 8, borderRadius: 999, background: 'var(--surface-muted)' }}>
                  <div
                    style={{
                      width: `${storageUsagePercent}%`,
                      height: '100%',
                      borderRadius: 999,
                      background: storageUsagePercent >= 90 ? '#ef4444' : '#f59e0b',
                    }}
                  />
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                  {t('assets.storagePercent', `${storageUsagePercent}% of plan storage in use`)}
                </div>
              </>
            )}
          </div>
        </div>

        {loading ? <p>{t('assets.loading', 'Loading...')}</p> : assets.length === 0 ? (
          <div className="empty-state"><p>{t('assets.empty', 'No assets uploaded')}</p></div>
        ) : (
          <table className="table">
            <thead><tr><th>{t('assets.table.name', 'Name')}</th><th>{t('assets.table.type', 'Type')}</th><th>{t('assets.table.size', 'Size')}</th><th>{t('assets.table.sharedHelper', 'Shared Helper')}</th><th></th></tr></thead>
            <tbody>
              {assets.map((a) => (
                <tr key={a.id}>
                  <td>{a.name}</td>
                  <td>{a.type}</td>
                  <td>{formatBytes(getAssetByteSize(a))}</td>
                  <td>{a.is_shared_helper ? t('assets.yes', 'Yes') : t('assets.no', 'No')}</td>
                  <td><button className="btn-secondary" onClick={() => handleDelete(a.id)}><Trash2 size={14} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
